import {
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PostAdd as PostAddIcon,
} from '@material-ui/icons';
import { red } from '@material-ui/core/colors';
import { Alert } from '@material-ui/lab';

import {
  ButtonGroup,
  DynamicForm,
  FileViewer,
  Label,
  Modal,
} from 'components';
import { MIME_TYPES } from 'constants/files';
import { getSignedUrl, upload } from 'utils/aws/s3';
import { setPickedKeyValue } from 'utils/objects';
import DocumentIcon from 'assets/svg/documentsSVG/icons8-document.svg';
import CompressedIcon from 'assets/svg/documentsSVG/icons8-slides.svg';
import PresentationIcon from 'assets/svg/documentsSVG/icons8-document.svg';
import ImageIcon from 'assets/svg/documentsSVG/icons8-image.svg';
import VideoIcon from 'assets/svg/video-icon.svg';
import AudioIcon from 'assets/svg/documentsSVG/icons8-document.svg';
import SpreadsheetIcon from 'assets/svg/documentsSVG/icons8-document.svg';
import TextIcon from 'assets/svg/documentsSVG/icons8-document.svg';

import { DEFAULT_PROPS, PROP_TYPES } from './constants';
import useStyles from './styles';

const EXT_IMAGES = MIME_TYPES
  .filter((option) => option.type === 'image')
  .map((option) => option.name) || [];
const EXT_VIDEO = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'm4v', '3gp', 'wmv'];
const EXT_INLINE_PREVIEW = [
  ...EXT_IMAGES,
  'pdf',
];
const EXT_MODAL_PREVIEW = [
  ...EXT_INLINE_PREVIEW,
  ...EXT_VIDEO,
  'doc',
  'docx',
  'odt',
  'xls',
  'xlsx',
  'ods',
  'csv',
  'ppt',
  'pptx',
  'odp',
];

const iconGroups = {
  document: ['doc', 'docx', 'odt', 'rtf', 'pdf'],
  spreadsheet: ['xls', 'xlsx', 'ods', 'csv'],
  presentation: ['ppt', 'pptx', 'odp'],
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'],
  compressed: ['zip', 'rar', '7z', 'tar', 'gz'],
  text: ['txt', 'md', 'log', 'json', 'xml'],
  video: ['mp4', 'avi', 'mkv', 'mov', 'wmv'],
  audio: ['mp3', 'wav', 'ogg', 'flac'],
  default: [],
};

const ICON_BY_GROUP = {
  document: DocumentIcon,
  spreadsheet: SpreadsheetIcon,
  presentation: PresentationIcon,
  image: ImageIcon,
  compressed: CompressedIcon,
  text: TextIcon,
  video: VideoIcon,
  audio: AudioIcon,
  default: DocumentIcon,
};

// Mapa plano extensión -> ícono, construido una sola vez al cargar el módulo.
// Evita recorrer `iconGroups` en cada render de cada documento.
const ICON_BY_EXTENSION = Object.entries(iconGroups).reduce(
  (acc, [group, extensions]) => {
    extensions.forEach((ext) => {
      acc[ext] = ICON_BY_GROUP[group] ?? DocumentIcon;
    });

    return acc;
  },
  {},
);

const getIconByExtension = (extension) =>
  ICON_BY_EXTENSION[extension?.toLowerCase()] ?? DocumentIcon;

const getCleanExtension = (url = '') => {
  const [path] = String(url).replace(/[`"'\s]/g, '').trim().split('?');

  return (path.split('.').pop() || '').toLowerCase();
};

const normalizeDocuments = (rawValue) => {
  if (typeof rawValue === 'string') {
    try {
      const parsedValue = JSON.parse(rawValue);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  }

  return Array.isArray(rawValue) ? rawValue : [];
};

const isDocumentActive = (document) => {
  const active = document?.active;

  return active === true
    || (
      typeof active === 'string'
      && active.trim().toLowerCase() === 'true'
    );
};

function DocumentControl(props) {
  const { i18n, t } = useTranslation('translation', {
    keyPrefix: 'components.forms.document_control',
  });
  const {
    disabled = false,
    error,
    events = {},
    label,
    name: controlName,
    placeholder,
    onChange,
    settings: {
      awsMetadata = {},
      hasMetadata = false,
      readOnly = false,
      route = 'tmp/',
    } = {},
  } = props;
  const { value: rawValue = [] } = props;
  const value = normalizeDocuments(rawValue);

  const classes = useStyles();
  const [currentView, setCurrentView] = useState('preview');
  const [documents, setDocuments] = useState(Array.isArray(value) ? value : []);

  // Keep the internal document list aligned with explicit Formik updates,
  // including conditional resolvers that mark existing files as inactive.
  const prevRawValueRef = useRef(rawValue);
  useEffect(() => {
    if (Object.is(rawValue, prevRawValueRef.current)) return;

    prevRawValueRef.current = rawValue;
    setDocuments(normalizeDocuments(rawValue));
  }, [rawValue]);
  const [hasError, setHasError] = useState();
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState();
  const [nameFile, setNameFile] = useState();
  const [open, setOpen] = useState();

  // Evita propagar el onChange en el primer render (montaje). Solo se notifica
  // al formulario padre cuando el usuario realmente cambia los documentos,
  const isFirstDocumentsRun = useRef(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handlePreview = async function (document) {
    const signedUrl = await getSignedUrl(document, 120);
    const extension = getCleanExtension(document?.url);
    const mimeType = MIME_TYPES.find((option) => option.name === extension);

    setModal({
      canFullScreen: true,
      open: true,
      title: 'preview',
      download: true,
      data: {
        ...document,
        mimeType,
        signedUrl,
      },
    });
  };

  const handleShowModal = (item) => {
    setNameFile('');
    setFilesToUpload([]);

    setModal({
      open: true,
      title: 'select_files',
      download: false,
      data: {},
      item,
    });
  };

  const handleFileSave = async () => {
    if (!nameFile) {
      addError('Debes escribir el nombre del archivo');

      return;
    }

    setLoading(true);
    const uploaded = [];

    for await (const fileToUpload of filesToUpload) {
      const ext = ((fileToUpload?.name ?? '').split('.').pop() || '').toLowerCase();
      const s3File = await upload(
        `${route}${new Date().getTime()}.${String(Math.random()).substring(
          2,
          7,
        )}${ext ? `.${ext.toLowerCase()}` : ''}`,
        fileToUpload,
        awsMetadata,
      );

      uploaded.push({
        active: true,
        metadata: {},
        name: `${nameFile || ''}${filesToUpload.length > 1 ? (` ${uploaded.length + 1}`) : ''}`,
        uploaded: new Date().getTime(),
        url: s3File.Location,
      });
    };

    setDocuments([
      ...documents,
      ...uploaded,
    ]);
    setHasError('');
    setModal({
      open: false,
    });
  };

  const handleShowModalDelete = (index) => {
    const documentToDelete = documents
      .filter(isDocumentActive)[index];

    setOpen({
      document: documentToDelete,
      show: true,
    });
  };

  const handleDelete = (documentToDelete) => {
    setOpen({
      show: false,
    });

    setDocuments(documents.map((document) => (
      document === documentToDelete
        ? {
          ...document,
          active: false,
        }
        : document
    )));
  };

  const handleDownload = async function (image) {
    const url = await getSignedUrl(image, 120);

    window.open(url);
  };

  const handleDocumentClick = function (doc, ext) {
    if (EXT_MODAL_PREVIEW.includes(ext)) {
      handlePreview(doc);
    }
  };

  const addError = function (msg = '') {
    setHasError(msg);

    document.getElementById('modalInputFileName').focus();
  };

  useEffect(() => {
    // En el montaje no notificamos al padre: el valor inicial ya proviene de
    // él, así que propagar aquí solo provocaría re-renders innecesarios.
    if (isFirstDocumentsRun.current) {
      isFirstDocumentsRun.current = false;
      setLoading(false);
      return;
    }

    const target = {
      name: controlName,
      value: documents,
    };
    onChange({
      target,
    });

    if (events?.onChange) {
      events.onChange(target);
    }

    setLoading(false);
  }, [documents]);

  return (
    <>
      <Modal
        actions={[
          {
            color: 'primary',
            icon: <CloseIcon />,
            label: t('modal.actions.no'),
            fn: function () {
              setOpen({
                show: false,
              });
            },
          },
          {
            color: 'error',
            icon: <CheckIcon />,
            label: t('modal.actions.yes'),
            fn: function () {
              handleDelete(open?.document);
            },
          },
        ]}
        maxWidth="xs"
        open={open?.show}
        title={t('messages.delete')}
      >
        {open?.document?.metadata?.is_verified && (
          <Alert
            children="Este documento esta verificado"
            severity="warning"
          />
        )}
      </Modal>

      <Modal
        actions={[
          {
            color: 'primary',
            hidden: readOnly || currentView !== 'metadata',
            icon: <EditIcon />,
            label: t('edit'),
            fn: function () {
              const objDocuments = structuredClone(documents);
              const idx = objDocuments.findIndex((doc) => doc.url === modal?.data?.url);

              objDocuments[idx].metadata = modal?.data?.metadata ?? {};

              setDocuments(objDocuments);
              setModal({
                open: false,
              });
            },
          },
          {
            color: 'primary',
            icon: <CloudDownloadIcon />,
            label: t('modal.actions.download'),
            hidden: !modal?.download || currentView === 'metadata',
            fn: () => handleDownload(modal?.data),
          },
          {
            color: 'primary',
            disabled: !filesToUpload.length || loading,
            icon: loading ? <CircularProgress size={20} /> : <CloudUploadIcon />,
            label: t('modal.actions.upload'),
            hidden: modal?.download,
            fn: handleFileSave,
          },
        ]}
        canFullScreen={modal?.canFullScreen}
        events={{
          onClose: !loading ? () => {
            setHasError('');
            setModal();
          } : undefined,
        }}
        maxWidth='md'
        open={modal?.open}
        title={t(modal?.title)}
      >
        {(!modal?.item && hasMetadata) && (<ButtonGroup
          actions={[
            {
              label: t('preview'),
              value: 'preview',
            },
            {
              label: t('metadata'),
              value: 'metadata',
            },
          ]}
          onChange={setCurrentView}
          pb={2}
          value={currentView}
          width={isMobile ? 'auto' : 180}
        />)}

        {currentView === 'metadata' && (<>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell children="Parámetro" />
                <TableCell children="Valor" />
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                {
                  label: 'Está verificado',
                  name: 'is_verified',
                  type: 'boolean',
                },
                {
                  label: 'Fecha de vigencia',
                  name: 'validity_date',
                  type: 'date',
                },
              ].map((item, index) => (<TableRow key={index}>
                <TableCell children={item.label} />
                <TableCell>
                  {(function () {
                    let paramValue;
                    if (modal?.data?.metadata !== undefined) {
                      paramValue = modal?.data?.metadata[item.name];
                    }

                    switch (item.type) {
                      default: break;

                      case 'boolean': {
                        if (readOnly) {
                          paramValue = (<Label
                            children={paramValue ? 'Si' : 'No'}
                            color={paramValue ? 'success' : 'error'}
                          />);
                        } else {
                          paramValue = (<DynamicForm
                            fields={{
                              events: {
                                onChange: function (event) {
                                  const objModal = structuredClone(modal);

                                  setPickedKeyValue(objModal, 'data.metadata.is_verified', event.value);
                                  setModal(objModal);
                                },
                              },
                              type: 'switch',
                              value: paramValue,
                            }}
                          />);
                        }
                        break;
                      }

                      case 'date': {
                        if (!readOnly) {
                          paramValue = (<DynamicForm
                            fields={{
                              events: {
                                onChange: function (event) {
                                  const objModal = structuredClone(modal);
                                  const [date] = event.value.toISOString().split('T');

                                  setPickedKeyValue(objModal, 'data.metadata.validity_date', date);
                                  setModal(objModal);
                                },
                              },
                              type: 'date',
                              value: new Date(`${paramValue}T00:00:00.000`),
                            }}
                          />);
                        }
                        break;
                      }
                    }

                    return paramValue;
                  })()}
                </TableCell>
              </TableRow>))}
            </TableBody>
          </Table>
        </>)}

        {!modal?.item && (
          <Box display={currentView === 'preview' ? undefined : 'none'}>
            <FileViewer
              data={modal}
              actions={true}
              thumbs={true}
              ownModal={true}
            />
          </Box>
        )}

        {modal?.item && (<Grid container>
          <input
            multiple
            id={`file_${modal?.item?.field}`}
            name={modal?.item?.filename}
            type="file"
            style={{
              display: 'none',
            }}
            onChange={({ target }) => {
              const count = target.files.length;
              const files = [];
              for (let i = 0; i < count; i++) {
                files.push(target.files[i]);
              }

              if (files.length > 0) {
                const firstFileName = files[0]?.name || '';
                const cleanName = firstFileName.includes('.')
                  ? firstFileName.substring(0, firstFileName.lastIndexOf('.'))
                  : firstFileName;

                setNameFile(cleanName);
              }

              setFilesToUpload([
                ...filesToUpload,
                ...files,
              ]);
            }}
          />
          <Grid item xs={12}>

            {hasError ?
              (<Alert
                children={i18n.exists(hasError) ? t(hasError) : hasError}
                onClose={() => addError()}
                severity="error"
              />)
              : (<Typography
                children={t('name_label')}
                variant="body2"
              />)}
            <TextField
              fullWidth
              id="modalInputFileName"
              name="file_name"
              onChange={({ target }) => setNameFile(target.value)}
              required
              size='small'
              type="text"
              value={nameFile}
              variant="outlined"
            />
          </Grid>
          <Grid item md={12}>
            <Box mt={1} textAlign="center">
              <Tooltip title={t('add_files_tooltip')}>
                <Button
                  children={t('modal.actions.browse_files')}
                  color="primary"
                  disabled={loading}
                  onClick={function () {
                    document.getElementById(`file_${modal?.item?.field}`).click();
                  }}
                  startIcon={<PostAddIcon />}
                  size="medium"
                  variant="contained"
                />
              </Tooltip>
            </Box>
          </Grid>

          {filesToUpload.length > 0 && <Grid item xs={12}>
            <Paper
              style={{
                marginTop: 10,
              }} elevation={2}
            >
              <List dense>
                {filesToUpload.map((fileItem, fileIndex) => (<ListItem
                  button
                  key={fileIndex}
                >
                  <ListItemAvatar
                    children={<Avatar children={<InsertDriveFileIcon />} />}
                  />
                  <ListItemText
                    primary={fileItem.name}
                    secondary={<>
                      <b>{Math.round(fileItem.size / 10.24, 2) / 100} KB</b>
                      {' '}{fileItem.type}
                    </>}
                  />
                  {!loading && (<ListItemSecondaryAction>
                    <Tooltip title={t('delete_document')}>
                      <IconButton edge="end" aria-label="delete">
                        <CancelIcon
                          onClick={function () {
                            setFilesToUpload(filesToUpload.filter((_, i) => i !== fileIndex));
                          }}
                          style={{
                            color: red[500],
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>)}
                </ListItem>))}
              </List>
            </Paper>
          </Grid>}

        </Grid>)}
      </Modal >

      <fieldset style={{
        borderRadius: 5,
        borderColor: error ? 'red' : undefined,
      }}>
        {label && <legend style={{
          marginLeft: 8,
        }}>
          <Typography color="textSecondary" variant="caption">
            <Box style={{
              marginLeft: 5,
              marginRight: 5,
            }}>
              {label}
            </Box>
          </Typography>
        </legend>}
        {placeholder && <Typography color="textPrimary" variant="caption">
          <Box
            children={placeholder}
            style={{
              marginLeft: 5,
              marginRight: 5,
            }}
          />
        </Typography>}
        {error && <Typography color="error" variant="caption">
          <Box
            children={error}
            style={{
              marginLeft: 5,
              marginRight: 5,
            }}
          />
        </Typography>}
        <ImageList
          className={classes.imageList}
          cols={5}
          style={{
            alignItems: 'left',
            justifyContent: 'left',
          }}
        >
          {!readOnly && <IconButton
            className={classes.button}
            disabled={disabled}
            onClick={() => handleShowModal(value)}
            style={{
              width: '50%',
            }}
          >
            <Tooltip
              children={<SvgIcon children={<AddIcon />} />}
              title="Agregar archivo"
            />
          </IconButton>}
          {
            (Array.isArray(documents) ? documents : [])
              .filter(isDocumentActive)
              .map((doc, filteredIndex) => {
                const ext = getCleanExtension(doc?.url);

                return (
                  <ImageListItem
                    key={`${doc.name}_${filteredIndex}`}
                    style={{
                      border: doc?.metadata?.is_verified
                        ? '2px solid #008a00'
                        : undefined,
                      padding: 5,
                      minWidth: '30%',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleDocumentClick(doc, ext)}
                  >
                    {/*
                      Rendimiento: en la galería se muestra SIEMPRE un ícono
                      genérico por extensión. El preview real (y la firma de la
                      URL) se cargan bajo demanda al hacer click en el documento
                      (handleDocumentClick -> handlePreview).
                    */}
                    <img
                      key={`${doc.name}_${filteredIndex}_icon`}
                      alt={doc.name}
                      src={getIconByExtension(ext)}
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'contain',
                        cursor: 'pointer',
                        margin: '20px auto',
                      }}
                    />
                    <ImageListItemBar
                      title={doc.name}
                      classes={{
                        root: classes.titleBar,
                        title: classes.title,
                      }}
                      actionIcon={
                        readOnly ? (
                          doc?.metadata?.is_verified ? (
                            <Tooltip
                              children={
                                <CheckIcon className={classes.verified} />
                              }
                              title="Documento verificado"
                            />
                          ) : undefined
                        ) : (
                          <Tooltip
                            children={
                              <IconButton
                                aria-label={`star ${doc.name}`}
                                children={
                                  <DeleteIcon className={classes.title} />
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleShowModalDelete(filteredIndex);
                                }}
                              />
                            }
                            title={t('delete_document')}
                          />
                        )
                      }
                    />
                  </ImageListItem>
                );
              })
          }
        </ImageList>
      </fieldset>

    </>
  );
}

DocumentControl.defaultProps = DEFAULT_PROPS;
DocumentControl.propTypes = PROP_TYPES;

// Memoizado: el formulario de personas re-renderiza con frecuencia (cada
// cambio de campo). Sin memo, todas las galerías de documentos se re-renderizan
// aunque sus props no cambien. `memo` evita ese trabajo cuando las props son
// las mismas.
export default memo(DocumentControl);
